import React, { startTransition, useMemo, useState } from "react";
import { Col, Row, Space } from "antd";
import { COREButton } from "../../COREDesignSystem/Action/COREButton";
import { COREIcon } from "../../COREDesignSystem/Content/COREIcon";
import { CORETag } from "../../COREDesignSystem/Content/CORETag";
import {
  COREDropdownMenu,
  DropdownMenuItem,
} from "../../COREDesignSystem/Form/COREDropdownMenu";
import { COREMenuItem } from "../../COREDesignSystem/Navigation/COREMenuItem";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { COREBody } from "../../COREDesignSystem/Typography/COREBody";
import { CORELoading } from "../../COREDesignSystem/Feedback/CORELoading";
import "./MarketsStatusIndicatorButton.less";
import { useBreakpoint } from "../customHoooks/useBreakpoint";
import { useDeepCompareEffect } from "react-use";
import equal from "fast-deep-equal";
import {
  transformMarketsToIncludeMarketStatus,
  useMarketSchedules,
} from "../customHoooks/useMarketSchedules";
import { MarketSchedules } from "../../openapi-typescript/common/market_schedules";
import classNames from "classnames";

const sortDataByMarketStatus = (marketSchedules: MarketSchedules[]) => {
  return transformMarketsToIncludeMarketStatus(marketSchedules).sort((a, b) =>
    a.isMarketOpen === b.isMarketOpen
      ? (a.marketName || "").localeCompare(b.marketName || "")
      : Number(b.isMarketOpen) - Number(a.isMarketOpen)
  );
};

const generateMarketStatusMenu = (
  marketSchedules: MarketSchedules[]
): DropdownMenuItem[] => {
  return [
    {
      isGroupMenu: true,
      closeWhenClick: true,
      groupHeaderProps: {
        title: "Markets status",
      },
      key: "add-company-menu-item",
      component: marketSchedules.map((data) => {
        return (
          <COREMenuItem
            testID={`page-live-market-status-indicator-${data.marketName}`}
            key={`page-live-market-status-indicator-${data.marketName}`}
            to={data.pageUrl || undefined}
          >
            <Row wrap={false} align={"middle"}>
              <Col flex="70px">
                {data?.isMarketOpen ? (
                  <CORETag
                    label={"Open"}
                    type="colourful"
                    colour="lime100"
                    tagStyle="solid"
                  />
                ) : (
                  <CORETag
                    label="Closed"
                    type="colourful"
                    colour="indigo80"
                    tagStyle="solid"
                  />
                )}
              </Col>
              <Col flex="auto" className={"market-status-text-no-wrap"}>
                <COREBody
                  marginBottom={false}
                  type="p3"
                  testID={`page-live-market-status-indicator-${data.marketName}-label`}
                >
                  {data?.marketName}
                </COREBody>
              </Col>
            </Row>
          </COREMenuItem>
        );
      }),
    },
  ];
};

const MarketStatusDropdownMenu = React.memo(
  ({
    sortedMarketSchedules,
    marketIsOpen,
  }: {
    sortedMarketSchedules: MarketSchedules[];
    marketIsOpen: boolean;
  }) => {
    const isMobileScreen = ["xxs"].includes(useBreakpoint());
    const dropdownMenuItems: DropdownMenuItem[] = useMemo(() => {
      return generateMarketStatusMenu(sortedMarketSchedules);
    }, [sortedMarketSchedules]);

    return (
      <div
        className={classNames("market-status-indicator", {
          "market-status-close": !marketIsOpen,
        })}
      >
        <COREDropdownMenu
          interactionsMenu={dropdownMenuItems}
          testID="page-live-market-status-indicator-dropdown"
          isDisabled={false}
          placement="bottomCenter"
          overlayStyle={{
            zIndex: 2001,
            width: "288px",
          }}
          className="market-status-indicator-dropdown"
        >
          <COREButton
            type="link"
            size="sm"
            testID="page-live-market-status-indicator-button"
            icon={
              <COREIcon
                icon={icon({ name: "tower-broadcast", style: "solid" })}
              />
            }
          >
            <Space size={2}>
              {!isMobileScreen && <>LIVE</>}
              <COREIcon icon={icon({ name: "caret-down", style: "solid" })} />
            </Space>
          </COREButton>
        </COREDropdownMenu>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps &&
    equal(prevProps.sortedMarketSchedules, nextProps.sortedMarketSchedules) &&
    prevProps.marketIsOpen === nextProps.marketIsOpen
);

const MarketStatusIndicatorButtonRealTime = React.memo(
  ({ marketSchedules }: { marketSchedules: MarketSchedules[] }) => {
    const [marketIsOpen, setMarketIsOpen] = useState<boolean>(false);
    const [sortedMarketSchedules, setSortedMarketSchedules] = useState<
      MarketSchedules[]
    >([]);

    useDeepCompareEffect(() => {
      const checkFunction = () => {
        startTransition(() => {
          const newMarketSchedules = sortDataByMarketStatus(marketSchedules);
          setSortedMarketSchedules(newMarketSchedules);
          setMarketIsOpen(newMarketSchedules.some((item) => item.isMarketOpen));
        });
      };

      const intervalId = setInterval(() => {
        checkFunction();
      }, 1000);

      checkFunction();

      return () => {
        clearInterval(intervalId);
      };
    }, [marketSchedules]);

    return (
      <MarketStatusDropdownMenu
        sortedMarketSchedules={sortedMarketSchedules}
        marketIsOpen={marketIsOpen}
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps && equal(prevProps.marketSchedules, nextProps.marketSchedules)
);

export const MarketStatusIndicatorButton = React.memo(
  function MarketStatusIndicatorButton() {
    const { loading, sync, error, updatable } = useMarketSchedules({
      isActive: true,
    });
    const marketSchedules = React.useMemo(
      () => updatable?.flatMap((u) => (u.data ? [u.data] : [])),
      [updatable]
    );

    if (loading && !sync) return <CORELoading />;
    if (error || !marketSchedules.length) return null;

    return (
      <MarketStatusIndicatorButtonRealTime marketSchedules={marketSchedules} />
    );
  }
);
